import { ArrowRight, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ValidatedInput } from '../../../components/design-system/auth';
import { SignupErrorCode } from '../../../modules/signup/signup-errors';
import { BookDemoPrompt } from '../../../modules/signup/BookDemoDialog';
import { SignupError } from './SignupError';

/** Loose on purpose: "acme.com", "www.acme.com/shop" and "https://acme.com" are all websites. */
export function looksLikeWebsite(value: string): boolean {
    const bare = (value || '').trim().replace(/^https?:\/\//i, '').split(/[/?#]/)[0];
    return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(bare) && !/^\d+(\.\d+){3}$/.test(bare);
}

/** Step 1: the store's website — everything else in the account hangs off it. */
export const WebsiteStep = ({
    value,
    onChange,
    onSubmit,
    busy,
    error,
    showInvalid,
    onBookDemo,
}: {
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    busy: boolean;
    error: SignupErrorCode | null;
    showInvalid: boolean;
    /** "Sell only in a showroom, or no website yet?" — opens Book a demo. */
    onBookDemo?: () => void;
}) => {
    const { t } = useTranslation();
    return (
        <form
            noValidate
            aria-busy={busy}
            onSubmit={(event) => {
                event.preventDefault();
                onSubmit();
            }}
            data-testid="signup-website-step"
        >
            <fieldset disabled={busy} className="contents">
                <ValidatedInput
                    label={t('SIGNUP.WEBSITE.LABEL')}
                    placeholder={t('SIGNUP.WEBSITE.PLACEHOLDER')}
                    value={value}
                    onChange={onChange}
                    autoComplete="url"
                    error={showInvalid}
                    errorMessage={t('SIGNUP.WEBSITE.INVALID_FORMAT')}
                    startIcon={<Globe />}
                    className="animate-form-item animate-delay-2"
                />
                <p className="animate-form-item animate-delay-2 mt-2 text-xs leading-relaxed text-muted-foreground">{t('SIGNUP.WEBSITE.HINT')}</p>

                {error && <SignupError code={error} />}

                <Button type="submit" variant="outline" className="animate-form-item animate-delay-3 mt-6 h-11 w-full" data-testid="signup-website-submit">
                    {busy ? (
                        <>
                            <Spinner aria-label={t('SIGNUP.WEBSITE.CHECKING')} />
                            {t('SIGNUP.WEBSITE.CHECKING')}
                        </>
                    ) : (
                        <>
                            {t('SIGNUP.WEBSITE.SUBMIT')}
                            <ArrowRight className="rtl:rotate-180" data-icon="inline-end" />
                        </>
                    )}
                </Button>
            </fieldset>
            {onBookDemo && (
                <BookDemoPrompt
                    text={t('SIGNUP.DEMO.PROMPT_WEBSITE')}
                    onClick={onBookDemo}
                    className="animate-form-item animate-delay-4 mt-5 text-center text-xs leading-relaxed text-muted-foreground"
                    testId="signup-book-demo"
                />
            )}
        </form>
    );
};
